/**
 * Decoded points that stay on the map, and survive a reload (`V123`, P20.1).
 *
 * ## Why anything is stored at all
 *
 * **A decoded sequence cannot be recovered by repeating the click.** `peptide_vis/decode.py` seeds
 * nothing: `decode_click` builds the sampler from `sampler`/`sampler_params` and no generator is
 * threaded anywhere on the path, so `temperature`, `top_k` and `top_p` all give a different
 * sequence for the same coordinate. Only `greedy` is reproducible. An in-memory list would
 * therefore lose something on reload that no gesture can bring back — which is why this module
 * exists rather than a `Map` on `Pane` beside `pins`.
 *
 * For the same reason **nothing is ever re-decoded on load**: the record holds the server's own
 * `DecodeResponse` verbatim. Replaying `POST /decode` at boot would show a *different* sequence
 * under the marker that had shown one, and would fire one model round trip per restored point.
 *
 * ## Why `localStorage` and not the other three
 *
 * | Where | Ruled out because |
 * |---|---|
 * | in memory | loses the unrecoverable thing above on every reload |
 * | the URL | messy, and it would have to carry the sequences: NCAA codes are hyphen-separated Titlecase triples, so twenty marks is a multi-kilobyte URL rewritten on every control change |
 * | the server | there is no user. `V8` is one shared password and no username, so a server-side store is one global list and several people's work would overlap in it |
 *
 * `panels/resize.ts` is the precedent for the mechanism, down to the key prefix.
 *
 * ## The key is the atlas **build**, not the atlas id (`V123`)
 *
 * A mark is a coordinate, and a coordinate only means something under one fitted projection of one
 * built atlas. The app already enforces the within-session half of this — `main.ts` clears the
 * pinned cards on a projection change and on an atlas change, because `x`/`y` are *that
 * projection's* coordinates.
 *
 * Durability opens a second hole that no existing rule covers. Rebuild `ancestor_10m_08_20` and
 * refit its UMAP: the atlas id is unchanged, the projection name is unchanged, and every stored
 * `xy` now names a region of a different embedding. So `built_at` is part of the view key, and a
 * record whose `built_at` no longer matches the loaded manifest is **deleted on read** rather than
 * drawn — see `forView`. That is the one place stale records are collected; nothing else prunes.
 *
 * ## Every operation re-reads storage first
 *
 * There is no in-memory cache to invalidate. Two tabs on the same origin share one store, and a
 * cached copy in either would clobber the other's writes on the next mutation. Marks are a few
 * hundred bytes and mutations are user-paced, so read-modify-write costs nothing and removes the
 * question.
 */
import type { DecodeResponse, LeafRef } from './api';

/** `v1` in the key, so a future shape change is a new key rather than a parse of a stale record. */
export const STORAGE_KEY = 'peptide-vis.decoded.v1';

/**
 * The most marks kept for one `(atlas, projection, build)`, oldest evicted first.
 *
 * Two ceilings, and this is under both. A record is ~300 bytes, so 200 of them is ~60 kB against a
 * ~5 MB origin quota. And 200 diamonds over one canvas is already past what a reader can take in —
 * the DOM overlay is the right mechanism for tens of nodes, not for thousands.
 */
export const CAP_PER_VIEW = 200;

/** What makes two coordinates comparable: one projection, of one build, of one atlas. */
export interface DecodedView {
  atlasId: string;
  projection: string;
  /** `AtlasManifest.built_at`. The invalidator — see the module docstring. */
  builtAt: string;
}

/**
 * What makes one interpolation step a step **of a run**, rather than a decode that happened to
 * arrive with others (`V143`, P30.7).
 *
 * ## Why an interpolated point is a decoded point at all
 *
 * `V123` defines a decoded point as *a sequence the model wrote at a latent the reader specified*.
 * An interpolation step is exactly that, with the latent arriving by arithmetic instead of by a
 * click — so everything this store already decides is right for it: the build key invalidates the
 * coordinate, the verbatim response is kept because a stochastic sampler makes the sequence
 * unrecoverable, and the amber diamond says the model wrote the text. A fourth store would have
 * been a third copy of the boilerplate below, a fourth glyph class on a map `marks.ts` already
 * records as having one too many at low zoom, and a fourth arm on `trajectory.ts`'s union.
 *
 * ## `recipe`, and why the latent itself is not here
 *
 * Measured: a 256-float latent is **5,243 characters** as JSON and **696** as base64 float16,
 * against a ~300-byte record and a **5,242,000 character** origin budget (measured in Chromium,
 * counted per character rather than per UTF-8 byte). One on every record in both stores is 7.99 MB
 * at ten full views — 152% of the budget, past which `commit` latches to memory and a reload loses
 * everything.
 *
 * The recipe is **170–478 characters** for the shapes that occur, and it is also the *more
 * faithful* of the two: it reproduces the latent to 1.24e-6 where base64 float16 moves it by
 * 9.63e-4. `V138` rejected storing the vector, and re-measuring at the smaller encoding did not
 * overturn it.
 *
 * `checkpointSha` is what makes the recipe safe. A stored latent is a record of what happened; a
 * recipe is a promise to recompute, and the recomputation is the same computation only while the
 * model is. The projection half of that is already guarded — a `builtAt` mismatch deletes the
 * record — and the model half was guarded nowhere, because this record did not carry a sha.
 * `EncodedMark` carries one for exactly this discrimination (`V139`).
 */
export interface InterpolationRun {
  /** One run. What groups the steps in the panel, and what "clear this run" acts on. */
  id: string;
  /**
   * Where this step sits on the line, `0 < t < 1`.
   *
   * **This is the run's ordering, and the step *number* is deliberately not stored beside it.**
   * `trajectory.ts` states the rule this follows: a stored ordinal is a second source of truth for
   * the set's own order, and the first change that forgets to rewrite it leaves two records
   * claiming to be step 3. The set here really does change — the endpoint-match switch places or
   * unplaces steps of a run already on the map — so `step i of n` is computed from `forRun` at the
   * moment a card is drawn.
   */
  t: number;
  /**
   * The `t` span this step stands for, when it represented a run of duplicates.
   *
   * A step is drawn at the **first** `t` that produced its sequence (`V145`), so a card that
   * printed only that number would read as a single sample when it is the start of a stretch.
   */
  tRange: [number, number];
  /** The path rule. `'linear'` today; a second rule would be a new value, not a new meaning. */
  path: 'linear';
  /** The flattened affine combination this step's latent is rebuilt from (`V144`). */
  recipe: [LeafRef, number][];
  /** `AtlasManifest.checkpoint_sha256` at the time of the run. */
  checkpointSha: string;
  /** `originOf` for each endpoint, for the card: `row 918`, `Thanatin-2`, `generated at (…)`. */
  from: string;
  to: string;
}

export interface DecodedMark extends DecodedView {
  id: string;
  xy: [number, number];
  /** The server's answer, verbatim. Never regenerated. */
  result: DecodeResponse;
  /** ISO instant of the decode. */
  at: string;
  /** Whether the on-map card is open beside the diamond. The diamond itself is always drawn. */
  card: boolean;
  /**
   * Present only on a point an interpolation placed (`V143`).
   *
   * **Optional, and `isMark` does not require it**, which is what makes this phase additive: every
   * record written before it parses unchanged and is drawn unchanged. A reader of this field must
   * treat `undefined` as *an ordinary decoded click*, never as a malformed run.
   */
  run?: InterpolationRun;
}

export interface DecodedStore {
  /**
   * Every mark for one view, oldest first, **after deleting the stale builds of that same
   * projection**. The one place pruning happens.
   */
  forView(view: DecodedView): DecodedMark[];
  /**
   * Record one decode. Returns the mark even when the write failed, so the map still draws it.
   *
   * `run` is given only by an interpolation (`V143`); a click passes nothing and gets a record
   * shaped exactly as it was before this phase.
   */
  add(
    view: DecodedView,
    xy: [number, number],
    result: DecodeResponse,
    run?: InterpolationRun,
  ): DecodedMark;
  /**
   * Every mark of one run, oldest first — what "clear this run" acts on and what the panel lists.
   *
   * **Across views deliberately.** A run is a set of records with one id; the panel only ever
   * shows one view's worth, but the removal must not leave orphans behind if the reader switches
   * projection between placing a run and clearing it.
   */
  forRun(runId: string): DecodedMark[];
  remove(id: string): void;
  /**
   * Forget several marks in one write. What the "clear all" control calls.
   *
   * **Ids rather than a wipe, and that is what makes the button's label true.** The control is
   * labelled with the number of points *on screen*, and a `clear()` that emptied the key would
   * also silently drop the marks of every atlas the reader is not currently looking at.
   */
  removeMany(ids: readonly string[]): void;
  /** Open or close one mark's on-map card, so a reload restores what the reader left. */
  setCard(id: string, open: boolean): void;
}

/**
 * `localStorage`, or `null` when it cannot be reached.
 *
 * **The property access itself throws** under some privacy settings, which is why this is in a
 * `try` rather than a null check. Every call site below is *also* guarded: a store that reads
 * today can still throw on `setItem` tomorrow when the origin's quota fills.
 */
function defaultStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const isXY = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  typeof value[0] === 'number' &&
  typeof value[1] === 'number' &&
  Number.isFinite(value[0]) &&
  Number.isFinite(value[1]);

/**
 * Whether one parsed entry is usable.
 *
 * **Per record, not per payload.** A single corrupt entry — written by an older build, or by a
 * half-completed write — must cost that entry and not the reader's whole history. Only a payload
 * that is not an array at all is discarded wholesale.
 */
function isMark(value: unknown): value is DecodedMark {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  const result = m['result'] as Record<string, unknown> | undefined;
  return (
    typeof m['id'] === 'string' &&
    typeof m['atlasId'] === 'string' &&
    typeof m['projection'] === 'string' &&
    typeof m['builtAt'] === 'string' &&
    typeof m['at'] === 'string' &&
    typeof m['card'] === 'boolean' &&
    isXY(m['xy']) &&
    typeof result === 'object' &&
    result !== null &&
    typeof result['sequence'] === 'string'
  );
}

const sameView = (a: DecodedView, b: DecodedView): boolean =>
  a.atlasId === b.atlasId && a.projection === b.projection && a.builtAt === b.builtAt;

/**
 * The store, over an injectable `Storage`.
 *
 * The parameter is what makes this unit-testable: the tests pass a stub that can be made to throw
 * on `setItem`, which is the failure that matters and the one a real browser will not produce on
 * demand. Passing `null` gives a store that keeps nothing — the honest degradation when the
 * browser will not let us write, and the app still draws the marks for the life of the page.
 */
export function createDecodedStore(storage: Storage | null = defaultStorage()): DecodedStore {
  /**
   * Whether writes are still landing.
   *
   * **Latching, and it has to be.** A store that only checked `storage === null` reads back an
   * empty list after a refused write — `getItem` succeeds and returns the *old* contents, or
   * nothing at all — so every decode would erase the one before it, which is the failure this
   * whole module exists to prevent. Once a write is refused the store keeps the full list in
   * memory instead and stops asking.
   */
  let persisting = storage !== null;

  /** The full list, kept only once `persisting` has gone false. Never a cache while it is true. */
  const volatile: DecodedMark[] = [];
  let counter = 0;

  function read(): DecodedMark[] {
    if (!persisting || storage === null) return [...volatile];
    let raw: string | null = null;
    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch {
      persisting = false;
      return [...volatile];
    }
    if (raw === null) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      // A payload that is not a list is not repairable into one; a *record* that is malformed
      // costs only itself.
      return Array.isArray(parsed) ? parsed.filter(isMark) : [];
    } catch {
      return [];
    }
  }

  /**
   * Make `next` the whole of the store, in memory if it cannot be in storage.
   *
   * Every mutation goes through here, so there is one place that decides what happens when the
   * browser refuses — and the answer is never "throw", because the caller is the decode path and
   * the sequence it is holding cannot be fetched again.
   */
  function commit(next: readonly DecodedMark[]): void {
    if (persisting && storage !== null) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(next));
        return;
      } catch {
        persisting = false;
      }
    }
    volatile.length = 0;
    volatile.push(...next);
  }

  return {
    forView(view) {
      const marks = read();
      // Same atlas and projection, different build: the coordinates are from an embedding that no
      // longer exists. Dropped here rather than merely hidden, so a rebuilt atlas does not leave
      // its old marks accumulating against the cap for ever.
      const fresh = marks.filter(
        (m) =>
          !(
            m.atlasId === view.atlasId &&
            m.projection === view.projection &&
            m.builtAt !== view.builtAt
          ),
      );
      if (fresh.length !== marks.length) commit(fresh);
      return fresh.filter((m) => sameView(m, view));
    },

    add(view, xy, result, run) {
      counter += 1;
      const mark: DecodedMark = {
        ...view,
        id: `${Date.now().toString(36)}-${counter.toString(36)}`,
        xy,
        result,
        at: new Date().toISOString(),
        // **Open, for a run exactly as for a click** (`V147`).
        //
        // This was `card: run === undefined` — `V142`'s rule for a bulk import, borrowed on the
        // assumption that a run is a bulk placement. It is not, and the difference is measured: an
        // import is up to 500 records, while a run places 7 at the default step count and saturates
        // near 13 whatever is asked for. A dozen open cards is what a dozen decode clicks already
        // produce.
        //
        // **And a closed card made the whole feature invisible.** `marks.ts` sizes a mark to
        // exactly what deepscatter draws a corpus point as — 2 px at k = 1 — so a run placed a
        // dozen 2 px diamonds into a cloud of 100,000 of them and said nothing. The reader could
        // not find the points the tool had just made. `#clear-pins` closes them all again in one
        // click, which is the escape hatch that makes this safe at the 50-step ceiling.
        card: true,
        ...(run === undefined ? {} : { run }),
      };
      const marks = read();
      const inView = marks.filter((m) => sameView(m, view));
      // Oldest first in the array, so the overflow to drop is the front of this view's slice.
      const evict = new Set(inView.slice(0, Math.max(0, inView.length + 1 - CAP_PER_VIEW)));
      commit([...marks.filter((m) => !evict.has(m)), mark]);
      return mark;
    },

    forRun(runId) {
      return read().filter((m) => m.run?.id === runId);
    },

    remove(id) {
      commit(read().filter((m) => m.id !== id));
    },

    removeMany(ids) {
      const dropped = new Set(ids);
      if (dropped.size === 0) return;
      commit(read().filter((m) => !dropped.has(m.id)));
    },

    setCard(id, open) {
      commit(read().map((m) => (m.id === id ? { ...m, card: open } : m)));
    },
  };
}
